import { useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';
import { KEYCLOAK_CONFIG } from '../../auth/keycloak';
import { SecurityDashboard } from './SecurityDashboard';
import { AuditLog } from './AuditLog';

type SettingsTab = 'security' | 'audit';

/** Deep link into the Keycloak admin console for the Hub's realm. */
const KEYCLOAK_CONSOLE_URL =
  `${KEYCLOAK_CONFIG.url}/admin/master/console/#/${KEYCLOAK_CONFIG.realm}`;

/** Combined settings page with Security and Audit sub-tabs */
export default function SettingsPage(): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<SettingsTab>('security');
  const { isDark } = useTheme();

  return (
    <div>
      <div className="vx-page-toolbar" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        borderBottom: `1px solid ${isDark ? COLORS.dark.border : COLORS.light.border}`,
      }}>
        <SubTabBar activeTab={activeTab} onTabChange={setActiveTab} isDark={isDark} />
        <KeycloakConsoleLink isDark={isDark} />
      </div>
      <div style={{ marginTop: '20px' }}>
        {activeTab === 'security' ? <SecurityDashboard /> : <AuditLog />}
      </div>
    </div>
  );
}

/** External link that opens the Keycloak admin console in a new tab. */
function KeycloakConsoleLink({ isDark }: { isDark: boolean }): React.JSX.Element {
  return (
    <a
      href={KEYCLOAK_CONSOLE_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '6px 14px', borderRadius: '6px',
        border: `1px solid ${isDark ? COLORS.dark.border : COLORS.light.border}`,
        fontFamily: FONTS.heading, fontSize: '11px',
        letterSpacing: '0.12em', textTransform: 'uppercase',
        textDecoration: 'none', whiteSpace: 'nowrap',
        color: isDark ? '#B0B0B0' : '#555',
      }}
    >
      Keycloak Admin
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    </a>
  );
}

/** Settings sub-tab bar */
function SubTabBar({ activeTab, onTabChange, isDark }: {
  activeTab: SettingsTab; onTabChange: (tab: SettingsTab) => void; isDark: boolean;
}): React.JSX.Element {
  const TABS: Array<{ id: SettingsTab; label: string }> = [
    { id: 'security', label: 'Security' },
    { id: 'audit', label: 'Audit Log' },
  ];

  return (
    <nav className="vx-tab-scroller" style={{
      display: 'flex', gap: '4px',
      paddingBottom: '12px',
    }}>
      {TABS.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          style={{
            padding: '6px 14px', borderRadius: '6px', border: 'none',
            fontFamily: FONTS.heading, fontSize: '11px',
            letterSpacing: '0.12em', textTransform: 'uppercase',
            cursor: 'pointer',
            color: activeTab === tab.id ? '#FFFFFF' : (isDark ? '#B0B0B0' : '#555'),
            backgroundColor: activeTab === tab.id
              ? (isDark ? 'rgba(1, 169, 237, 0.15)' : 'rgba(1, 101, 198, 0.1)')
              : 'transparent',
            transition: 'background-color 0.2s, color 0.2s',
          }}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}
